/**
 * Hook for managing a server-driven wizard instance.
 *
 * Uses TanStack Query for fetching and caching wizard state,
 * with mutations for step completion. Supports client-side navigation
 * offset and local form data accumulation.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWizardState,
  completeWizardStep,
  type WizardState,
} from "../services/naraDoor";

interface UseWizardInstanceReturn {
  /** Current wizard state from server */
  wizardState: WizardState | undefined;
  /** Whether the initial fetch is loading */
  isLoading: boolean;
  /** Error from fetching */
  error: Error | null;
  /** Effective step index (server + client offset) */
  effectiveStepIndex: number;
  /** Local form data accumulated across steps */
  localFormData: Record<string, string>;
  /** Update local form data */
  updateLocalFormData: (data: Record<string, string>) => void;
  /** Complete the current step on the server */
  completeStep: (stepId: string, data: Record<string, any>) => Promise<WizardState>;
  /** Whether a step completion is in progress */
  isCompleting: boolean;
  /** Navigate forward (client-side offset) */
  goNext: () => void;
  /** Navigate backward (client-side offset) */
  goBack: () => void;
  /** Reset client-side navigation offset */
  resetOffset: () => void;
}

export function useWizardInstance(instanceId: string): UseWizardInstanceReturn {
  const queryClient = useQueryClient();
  const [clientOffset, setClientOffset] = useState(0);
  const [localFormData, setLocalFormData] = useState<Record<string, string>>({});
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch wizard state
  const {
    data: wizardState,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["wizardState", instanceId],
    queryFn: () => getWizardState(instanceId),
    enabled: !!instanceId,
    staleTime: 1000 * 30, // 30 seconds
  });

  // Check if the current step is a polling/async step to enable auto-refresh
  useEffect(() => {
    if (!wizardState) return;

    const currentStep = wizardState.steps[wizardState.current_step_index];
    const isAsync =
      currentStep &&
      (currentStep.status === "waiting" || currentStep.status === "executing");

    if (isAsync) {
      // Poll every 3 seconds for async steps
      pollingIntervalRef.current = setInterval(() => {
        queryClient.invalidateQueries({
          queryKey: ["wizardState", instanceId],
        });
      }, 3000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [wizardState, instanceId, queryClient]);

  // Step completion mutation
  const completeMutation = useMutation({
    mutationFn: ({
      stepId,
      data,
    }: {
      stepId: string;
      data: Record<string, any>;
    }) => completeWizardStep(instanceId, stepId, data),
    onSuccess: () => {
      // Invalidate cache to re-fetch real state from Temporal
      // (complete-step returns {signaled: true}, not a WizardState)
      queryClient.invalidateQueries({ queryKey: ["wizardState", instanceId] });
      queryClient.invalidateQueries({ queryKey: ["userXp"] });
      setClientOffset(0);
    },
  });

  const effectiveStepIndex = Math.max(
    0,
    Math.min(
      (wizardState?.current_step_index ?? 0) + clientOffset,
      (wizardState?.steps.length ?? 1) - 1
    )
  );

  const updateLocalFormData = useCallback(
    (data: Record<string, string>) => {
      setLocalFormData((prev) => ({ ...prev, ...data }));
    },
    []
  );

  const completeStepFn = useCallback(
    async (stepId: string, data: Record<string, any>): Promise<WizardState> => {
      const merged = { ...localFormData, ...data };
      return completeMutation.mutateAsync({ stepId, data: merged });
    },
    [completeMutation, localFormData]
  );

  const goNext = useCallback(() => {
    const maxIndex = (wizardState?.steps.length ?? 1) - 1;
    const serverIndex = wizardState?.current_step_index ?? 0;
    if (serverIndex + clientOffset < maxIndex) {
      setClientOffset((prev) => prev + 1);
    }
  }, [wizardState, clientOffset]);

  const goBack = useCallback(() => {
    if (clientOffset > 0) {
      setClientOffset((prev) => prev - 1);
    } else {
      // Allow going back before the server's current step
      const serverIndex = wizardState?.current_step_index ?? 0;
      if (serverIndex + clientOffset > 0) {
        setClientOffset((prev) => prev - 1);
      }
    }
  }, [clientOffset, wizardState]);

  const resetOffset = useCallback(() => {
    setClientOffset(0);
  }, []);

  return {
    wizardState,
    isLoading,
    error: error as Error | null,
    effectiveStepIndex,
    localFormData,
    updateLocalFormData,
    completeStep: completeStepFn,
    isCompleting: completeMutation.isPending,
    goNext,
    goBack,
    resetOffset,
  };
}
